import type {FirstRecipientProgress} from "core/p2p/peerActivity";
import {SHORT_OF_THE_LINE} from "core/upload/uploadProgress";

/**
 * What the game's progress rail and finish line follow (kitecorp/transferify-ui#2872) — React-free.
 *
 * The rail IS the transfer, so it may never claim the finish before the transfer has one: short of a
 * confirmed completion it holds at 99% even when every byte is out, and only a completion moves it to
 * the line. The upload's completion is the upload call resolving; the Instant card's is the FIRST
 * recipient's download completing ({@link FirstRecipientProgress}).
 */
export interface GameProgress {
	/** 0 to 1. */
	fraction: number;
	done: boolean;
}

/** A transfer that has finished: the rail is full and the line is reached. The hosted success card's game is on it. */
export const FINISHED_TRANSFER: Readonly<GameProgress> = Object.freeze({fraction: 1, done: true});

function fraction(sent: number, total: number): number {
	return total > 0 ? Math.min(SHORT_OF_THE_LINE, sent / total) : 0;
}

export function uploadGameProgress(bytesSent: number, totalBytes: number, complete: boolean): GameProgress {
	return complete ? FINISHED_TRANSFER : {fraction: fraction(bytesSent, totalBytes), done: false};
}

export function p2pGameProgress(first: FirstRecipientProgress | null): GameProgress {
	if (first?.complete) {
		return FINISHED_TRANSFER;
	}
	return {fraction: first === null ? 0 : fraction(first.sentBytes, first.requestedBytes), done: false};
}

/**
 * Whether a game opened now races the transfer to a finish. Not once the transfer has finished: the
 * Instant card keeps offering the game while its link is live, and a round opened after the first
 * recipient's download has nothing left to race, so it has no finish.
 */
export function runHasFinish(progress: GameProgress): boolean {
	return !progress.done;
}
